import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Loader2, Check, AlertCircle, Upload } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { getMyReaderProfile, saveMyReaderProfile, getReaderPhotoUploadUrl, emptyReaderApplication, readerApplicationComplete, READER_APP_BACKGROUND, READER_APP_YEARS, READER_APP_READING, type ReaderProfile, type ReaderApplication } from '@/services/readerApi';
import { resolveMediaUrl } from '@/utils/mediaUrl';

// A reader's own profile. This is new: readers used to edit these fields on
// casting and Plus merely displayed them, which is how the roster could empty
// out without anyone noticing. It's owned here now.

// These MUST match the strings already on the roster, because the pills are how
// a reader sees and edits what's stored. They previously read 'Drama' and
// 'Television' while the roster holds 'Dramatic' (11 readers) and 'TV' (10) — so
// a reader's real categories showed as unselected, and clicking the pill that
// looked right left them holding both spellings.
const CATEGORY_OPTIONS = [
  'Comedy', 'Dramatic', 'Commercial', 'Film', 'TV',
  'Theatre', 'Voiceover', 'Improv', 'Animation', 'Musical Theatre',
];

// The form lives on a beige paper card, like every other reader surface. It used
// to sit directly on the dark app background with labels and help text in
// --hj-ink-2 / --hj-ink-3, i.e. faded grey on black: technically "secondary"
// tokens, in practice unreadable. Every one of these is now paper ink.
const label: React.CSSProperties = {
  display: 'block', fontSize: 13.5, fontWeight: 700,
  color: 'var(--hj-beige-ink)', marginBottom: 6,
};
const help: React.CSSProperties = { fontSize: 12.5, color: 'var(--hj-beige-ink-2)', margin: '6px 0 0', lineHeight: 1.5 };
const optional: React.CSSProperties = { fontWeight: 400, color: 'var(--hj-beige-ink-2)' };
const input: React.CSSProperties = {
  width: '100%', padding: '11px 13px', borderRadius: 10,
  border: '1px solid var(--hj-beige-line)', background: '#FFFFFF',
  color: 'var(--hj-beige-ink)', fontSize: 15, fontFamily: 'inherit',
};
const card: React.CSSProperties = {
  marginTop: 20, padding: '26px 24px', borderRadius: 'var(--hj-r-md)',
  background: 'var(--hj-beige)', border: '1px solid var(--hj-beige-line)',
  color: 'var(--hj-beige-ink)',
};

// A phone field people can actually type into, with the country picked
// explicitly rather than guessed from punctuation. Same countries casting
// supports for talent. US and Canada share +1, so the choice only changes the
// national-format hint; both dial the same.
const PHONE_COUNTRIES = [
  { code: 'US', label: 'US', dial: '1', digits: 10, hint: '(555) 123-4567' },
  { code: 'CA', label: 'Canada', dial: '1', digits: 10, hint: '(416) 555-0123' },
  { code: 'GB', label: 'UK', dial: '44', digits: 10, hint: '7400 123456' },
  { code: 'AU', label: 'Australia', dial: '61', digits: 9, hint: '412 345 678' },
];

const countryByCode = (c: string) => PHONE_COUNTRIES.find(x => x.code === c) || PHONE_COUNTRIES[0];

// Digits only, capped at that country's length. Formatting is North-American
// only; +44 and +61 have enough regional variation that grouping them wrongly
// looks more broken than leaving them alone.
const formatNational = (v: string, code: string) => {
  const c = countryByCode(code);
  const d = v.replace(/[^\d]/g, '').slice(0, c.digits);
  if (c.dial !== '1') return d;
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

// Stored and sent as E.164, which is what casting's Twilio hook expects.
const toE164 = (national: string, code: string) => {
  const d = national.replace(/[^\d]/g, '');
  return d ? `+${countryByCode(code).dial}${d}` : '';
};

// Split a stored E.164 number back into (country, national) for the form.
const fromE164 = (v: string | null | undefined) => {
  const raw = String(v || '').trim();
  if (!raw.startsWith('+')) return { code: 'US', national: formatNational(raw, 'US') };
  const d = raw.slice(1).replace(/[^\d]/g, '');
  for (const c of ['61', '44']) {
    if (d.startsWith(c)) {
      const hit = PHONE_COUNTRIES.find(x => x.dial === c)!;
      return { code: hit.code, national: formatNational(d.slice(c.length), hit.code) };
    }
  }
  if (d.startsWith('1')) return { code: 'US', national: formatNational(d.slice(1), 'US') };
  return { code: 'US', national: formatNational(d, 'US') };
};

// Rates are entered in dollars and stored in cents.
const toDollars = (c: number | null | undefined) => (c === null || c === undefined ? '' : String(c / 100));
const toCents = (s: string) => {
  const t = s.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null;
};

const ReaderProfileEditPage: React.FC = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [published, setPublished] = useState(false);
  // Approval is the team's gate; pending = asked to be listed, not yet reviewed.
  const [approved, setApproved] = useState(false);
  const [pending, setPending] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [revoked, setRevoked] = useState(false);
  // Katia's application. Complete it and the profile lists itself on save.
  const [app, setApp] = useState<ReaderApplication>(emptyReaderApplication());

  const [form, setForm] = useState({
    name: '', rate: '', directorRate: '', coachRate: '',
    categories: [] as string[], experience: '', availabilityNotes: '', timezone: '', calendlyUrl: '', phone: '',
    phoneCountry: 'US',
  });
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const onPickPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';                 // so re-picking the same file still fires
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Please choose an image file.'); return; }
    if (file.size > 10 * 1024 * 1024) { setError('That image is over 10MB — please pick a smaller one.'); return; }
    setUploadingPhoto(true);
    setError(null);
    try {
      const { uploadUrl, key } = await getReaderPhotoUploadUrl(file.name, file.type);
      const put = await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      if (!put.ok) throw new Error('Upload failed. Please try again.');
      setPhotoUrl(key);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not upload that image.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { profile } = await getMyReaderProfile();
      if (profile) {
        setForm({
          name: profile.name || '',
          rate: toDollars(profile.rateCents),
          directorRate: toDollars(profile.directorRateCents),
          coachRate: toDollars(profile.coachRateCents),
          categories: profile.categories || [],
          experience: profile.experience || '',
          availabilityNotes: profile.availabilityNotes || '',
          timezone: profile.timezone || '',
          phone: fromE164(profile.phone).national,
          phoneCountry: fromE164(profile.phone).code,
          calendlyUrl: profile.calendlyUrl || '',
        });
        setPhotoUrl(profile.photoUrl || null);
        setPublished(!!profile.published);
        setApproved(!!profile.approved);
        setPending(!!profile.wantsListed && !profile.approved);
        setRevoked(!!profile.revoked);
        if (profile.application) setApp({ ...emptyReaderApplication(), ...profile.application });
      } else {
        setIsNew(true);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load your profile.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Mirrors the server's rule, so the button explains itself before you press it.
  const missing: string[] = [];
  if (!toCents(form.rate) && !toCents(form.directorRate) && !toCents(form.coachRate)) missing.push('at least one rate');
  if (form.categories.length === 0) missing.push('at least one category');
  if (!form.experience.trim()) missing.push('a short experience note');
  // Readers who were listed before the application existed are not asked to
  // fill it in to stay listed; it's part of going live, not of staying live.
  if (!published && !readerApplicationComplete(app)) missing.push('the application below');

  const toggleCategory = (c: string) =>
    setForm(f => ({
      ...f,
      categories: f.categories.includes(c) ? f.categories.filter(x => x !== c) : [...f.categories, c],
    }));

  const save = async (wantPublished: boolean) => {
    setSaving(true);
    setError(null);
    try {
      const res = await saveMyReaderProfile({
        name: form.name,
        rateCents: toCents(form.rate),
        directorRateCents: toCents(form.directorRate),
        coachRateCents: toCents(form.coachRate),
        categories: form.categories,
        experience: form.experience,
        availabilityNotes: form.availabilityNotes,
        timezone: form.timezone,
        phone: toE164(form.phone, form.phoneCountry),
        photoUrl,
        calendlyUrl: form.calendlyUrl.trim() || null,
        application: app,
        published: wantPublished,
      } as Partial<ReaderProfile>);
      setPublished(res.published);
      setApproved(!!res.approved);
      setPending(!!res.pendingApproval);
      setIsNew(false);
      toast({
        title: res.published ? "You're listed" : res.pendingApproval ? 'Sent for review' : 'Saved',
        description: res.notice
          || (res.published
            ? 'Talent can find and book you on the readers page.'
            : 'Saved as a draft — nobody sees it until you go live.'),
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <main style={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
        <Loader2 className="animate-spin" size={22} style={{ color: 'var(--hj-ink-3)' }} />
      </main>
    );
  }

  return (
    <main style={{ background: 'var(--hj-bg)', color: 'var(--hj-ink)', minHeight: 'calc(100vh - 68px)' }}>
      <div className="mx-auto" style={{ maxWidth: 720, padding: '0 20px 80px' }}>
        <div style={{ padding: '20px 0 10px', fontSize: 13 }}>
          <Link to="/readers" style={{ color: 'var(--hj-ink)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <ChevronLeft size={13} /> Readers
          </Link>
        </div>

        <h1 className="font-hj-display m-0" style={{ fontSize: 32, letterSpacing: '-0.02em' }}>{isNew ? 'Set up your reader profile' : 'Your reader profile'}</h1>
        <p style={{ marginTop: 10, fontSize: 15, lineHeight: 1.55, color: 'var(--hj-ink)' }}>
          {isNew
            ? 'Readers run paid sessions with actors on Hey Joe Plus: you read opposite them, and they walk away with the take. Fill this in, send it for review, and the Hey Joe team will list you.'
            : "This is what talent sees when they're choosing a reader. You control it, and you can take yourself off the list at any time."}
        </p>

        <div style={card} data-paper="beige">

        <div style={{
          padding: '11px 14px', borderRadius: 10, fontSize: 13.5, fontWeight: 600,
          background: published ? '#FFFFFF' : 'var(--hj-beige-deep)',
          border: '1px solid var(--hj-beige-line)',
          color: 'var(--hj-beige-ink)',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          {published ? <Check size={15} /> : <AlertCircle size={15} />}
          {published
            ? "You're listed on the readers page."
            : revoked
              ? "Your listing was turned off by the Hey Joe team. Email hello@heyjoe.io if you think that's a mistake."
              : pending
                ? "Saved. Your listing needs a look from the Hey Joe team; you'll get an email when you're live."
                : 'Not listed yet. Finish everything below, including the application, and go live.'}
        </div>

        {/* There was nowhere to set this. The photo was whatever the migration
            happened to carry across, and a reader with none had no way to add one. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 20 }}>
          {photoUrl ? (
            <img src={resolveMediaUrl(photoUrl)} alt="" className="hj-headshot" style={{ width: 76, height: 95, flexShrink: 0 }} />
          ) : (
            <div className="hj-headshot" style={{ width: 76, height: 95, flexShrink: 0, background: 'var(--hj-beige-deep)', border: '1px dashed var(--hj-beige-line)' }} />
          )}
          <div>
            <label style={{ ...label, marginBottom: 4 }}>Your headshot</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, cursor: uploadingPhoto ? 'wait' : 'pointer',
                padding: '8px 14px', borderRadius: 999, background: '#FFFFFF',
                border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)',
                fontSize: 13, fontWeight: 600,
              }}>
                {uploadingPhoto ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                {uploadingPhoto ? 'Uploading…' : photoUrl ? 'Replace' : 'Upload a photo'}
                <input type="file" accept="image/*" style={{ display: 'none' }} disabled={uploadingPhoto} onChange={onPickPhoto} />
              </label>
              {photoUrl && !uploadingPhoto && (
                <button type="button" onClick={() => setPhotoUrl(null)} style={{
                  padding: '8px 14px', borderRadius: 999, background: 'transparent',
                  border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink-2)',
                  fontSize: 13, fontWeight: 600, cursor: 'pointer',
                }}>Remove</button>
              )}
            </div>
            <p style={help}>A clear, friendly shot of your face. Saved when you save the profile.</p>
          </div>
        </div>

        <div style={{ display: 'grid', gap: 20, marginTop: 24 }}>
          <div>
            <label style={label}>Name</label>
            <input style={input} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="How your name should appear" />
          </div>

          <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
            <div>
              <label style={label}>Reading rate <span style={optional}>per 15 min</span></label>
              <input style={input} inputMode="decimal" value={form.rate} onChange={e => setForm(f => ({ ...f, rate: e.target.value }))} placeholder="25" />
              <p style={help}>US dollars. A 30-minute session bills two of these. Set the rates you offer and leave the rest blank; one is enough to go live.</p>
            </div>
            <div>
              <label style={label}>Director rate <span style={optional}>per 15 min</span></label>
              <input style={input} inputMode="decimal" value={form.directorRate} onChange={e => setForm(f => ({ ...f, directorRate: e.target.value }))} placeholder="60" />
            </div>
            <div>
              <label style={label}>Coaching rate <span style={optional}>per 15 min</span></label>
              <input style={input} inputMode="decimal" value={form.coachRate} onChange={e => setForm(f => ({ ...f, coachRate: e.target.value }))} placeholder="45" />
            </div>
          </div>

          <div>
            <label style={label}>What you read for</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {CATEGORY_OPTIONS.map(c => {
                const on = form.categories.includes(c);
                return (
                  <button key={c} type="button" onClick={() => toggleCategory(c)} style={{
                    padding: '7px 14px', borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    background: on ? 'var(--hj-yellow)' : '#FFFFFF',
                    color: on ? 'oklch(0.18 0.005 70)' : 'var(--hj-beige-ink)',
                    border: `1px solid ${on ? 'var(--hj-yellow)' : 'var(--hj-beige-line)'}`,
                  }}>{c}</button>
                );
              })}
            </div>
          </div>

          <div>
            <label style={label}>A little about your experience</label>
            <textarea style={{ ...input, minHeight: 110, resize: 'vertical' }} value={form.experience}
              onChange={e => setForm(f => ({ ...f, experience: e.target.value }))}
              placeholder="A couple of lines on your background — what talent would want to know when picking a reader." />
          </div>

          <div>
            <label style={label}>When you're generally available <span style={optional}>(optional)</span></label>
            <input style={input} value={form.availabilityNotes} onChange={e => setForm(f => ({ ...f, availabilityNotes: e.target.value }))} placeholder="e.g. Weekday evenings and weekends" />
          </div>

          <div>
            <label style={label}>Calendly booking link <span style={optional}>(optional)</span></label>
            <input style={input} value={form.calendlyUrl} onChange={e => setForm(f => ({ ...f, calendlyUrl: e.target.value }))} placeholder="https://calendly.com/your-name" />
            <p style={help}>
              If you use Calendly, paste the address of your booking page and we'll show your open
              times on your profile so people stop guessing at one. Bookings still happen here, and
              you still get paid through Hey Joe.
            </p>
            <p style={{ ...help, marginTop: 4 }}>
              <strong>Add the session to your calendar when you're booked</strong> — we email you an
              invite. That's what stops Calendly offering the same slot to someone else.
            </p>
          </div>
          <div>
            <label style={label}>Time zone</label>
            {/* A picker, not a text box. The old free-text field suggested
                "America/Los_Angeles" while every stored value is a coarse label
                like "Pacific", so following the placeholder produced a value that
                matched nothing else on the roster. */}
            <select style={input} value={form.timezone} onChange={e => setForm(f => ({ ...f, timezone: e.target.value }))}>
              <option value="">Select your time zone…</option>
              {['Eastern', 'Central', 'Mountain', 'Pacific', 'Alaska', 'Hawaii'].map(tz => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
            <p style={help}>So session times show correctly for both of you.</p>
          </div>

          <div>
            <label style={label}>Mobile number <span style={optional}>(optional, but recommended)</span></label>
            <div style={{ display: 'flex', gap: 8 }}>
              <select
                style={{ ...input, width: 132, flex: '0 0 auto' }}
                value={form.phoneCountry}
                onChange={e => setForm(f => ({ ...f, phoneCountry: e.target.value, phone: formatNational(f.phone, e.target.value) }))}
              >
                {PHONE_COUNTRIES.map(c => (
                  <option key={c.code} value={c.code}>{c.label} +{c.dial}</option>
                ))}
              </select>
              <input
                style={input}
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={form.phone}
                onChange={e => setForm(f => ({ ...f, phone: formatNational(e.target.value, f.phoneCountry) }))}
                placeholder={countryByCode(form.phoneCountry).hint}
              />
            </div>
            <p style={help}>We text you when someone asks for a session. Email alone gets missed, which is the single biggest reason requests go unanswered. Reply STOP any time.</p>
          </div>

        </div>

        {error && (
          <p style={{ marginTop: 18, color: 'var(--hj-red, #c0392b)', fontSize: 13.5 }}>{error}</p>
        )}

        {/* The application. Filling it in completely is what lists you; there is
            no separate review step. The team reads every submission. */}
        <div style={{ marginTop: 28, paddingTop: 22, borderTop: '1px solid var(--hj-beige-line)' }}>
          <h2 className="font-hj-display m-0" style={{ fontSize: 22, letterSpacing: '-0.01em', color: 'var(--hj-beige-ink)' }}>Reader application</h2>
          <p style={help}>{published ? 'Five quick questions. You were listed before this existed, so it\'s optional for you, but we\'d love to have it on file.' : 'Five quick questions. Your name and email come from your account.'}</p>

          <div style={{ marginTop: 18 }}>
            <label style={label}>Professional background <span style={{ fontWeight: 400, color: 'var(--hj-beige-ink-2)' }}>(select all that apply)</span></label>
            <div className="flex flex-wrap" style={{ gap: 8, marginTop: 6 }}>
              {READER_APP_BACKGROUND.map(opt => {
                const on = app.background.includes(opt);
                return (
                  <button key={opt} type="button" onClick={() => setApp(a => ({ ...a, background: on ? a.background.filter(x => x !== opt) : [...a.background, opt] }))}
                    style={{ padding: '7px 13px', borderRadius: 999, fontSize: 13, fontFamily: 'inherit', cursor: 'pointer', border: `1px solid ${on ? 'var(--hj-beige-ink)' : 'var(--hj-beige-line)'}`, background: on ? 'var(--hj-beige-ink)' : '#FFFFFF', color: on ? 'var(--hj-beige)' : 'var(--hj-beige-ink)', fontWeight: on ? 700 : 500 }}>
                    {opt}
                  </button>
                );
              })}
            </div>
            {app.background.includes('Other') && (
              <input style={{ ...input, marginTop: 8 }} value={app.backgroundOther} onChange={e => setApp(a => ({ ...a, backgroundOther: e.target.value }))} placeholder="Tell us what" />
            )}
          </div>

          <div style={{ marginTop: 18 }}>
            <label style={label}>Years of professional experience</label>
            <div className="flex flex-wrap" style={{ gap: 8, marginTop: 6 }}>
              {READER_APP_YEARS.map(opt => {
                const on = app.yearsExperience === opt;
                return (
                  <button key={opt} type="button" onClick={() => setApp(a => ({ ...a, yearsExperience: opt }))}
                    style={{ padding: '7px 13px', borderRadius: 999, fontSize: 13, fontFamily: 'inherit', cursor: 'pointer', border: `1px solid ${on ? 'var(--hj-beige-ink)' : 'var(--hj-beige-line)'}`, background: on ? 'var(--hj-beige-ink)' : '#FFFFFF', color: on ? 'var(--hj-beige)' : 'var(--hj-beige-ink)', fontWeight: on ? 700 : 500 }}>
                    {opt}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ marginTop: 18 }}>
            <label style={label}>Briefly describe your professional experience.</label>
            <textarea style={{ ...input, minHeight: 100, resize: 'vertical' }} value={app.professionalExperience}
              onChange={e => setApp(a => ({ ...a, professionalExperience: e.target.value }))} />
          </div>

          <div style={{ marginTop: 18 }}>
            <label style={label}>How much experience do you have reading opposite actors for auditions or self-tapes?</label>
            <div style={{ display: 'grid', gap: 6, marginTop: 6 }}>
              {READER_APP_READING.map(opt => {
                const on = app.readingExperience === opt;
                return (
                  <button key={opt} type="button" onClick={() => setApp(a => ({ ...a, readingExperience: opt }))}
                    style={{ textAlign: 'left', padding: '9px 13px', borderRadius: 10, fontSize: 13.5, fontFamily: 'inherit', cursor: 'pointer', border: `1px solid ${on ? 'var(--hj-beige-ink)' : 'var(--hj-beige-line)'}`, background: on ? 'var(--hj-beige-ink)' : '#FFFFFF', color: on ? 'var(--hj-beige)' : 'var(--hj-beige-ink)', fontWeight: on ? 700 : 500 }}>
                    {opt}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ marginTop: 18 }}>
            <label style={label}>What do you think makes someone a strong audition reader?</label>
            <textarea style={{ ...input, minHeight: 100, resize: 'vertical' }} value={app.strongReader}
              onChange={e => setApp(a => ({ ...a, strongReader: e.target.value }))} />
          </div>
        </div>

        {missing.length > 0 && !published && (
          <p style={{ ...help, marginTop: 18 }}>
            To go live you still need {missing.join(', ').replace(/, ([^,]*)$/, ' and $1')}.
          </p>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 22, flexWrap: 'wrap' }}>
          <button type="button" disabled={saving} onClick={() => save(true)} style={{
            padding: '11px 22px', borderRadius: 999, border: 'none', cursor: saving ? 'wait' : 'pointer',
            background: 'var(--hj-yellow)', color: 'oklch(0.18 0.005 70)', fontWeight: 700, fontSize: 14,
            opacity: missing.length ? 0.55 : 1,
          }}>
            {saving ? 'Saving…' : published ? 'Save changes' : 'Go live'}
          </button>
          <button type="button" disabled={saving} onClick={() => save(false)} style={{
            padding: '11px 22px', borderRadius: 999, cursor: saving ? 'wait' : 'pointer',
            background: '#FFFFFF', border: '1px solid var(--hj-beige-line)',
            color: 'var(--hj-beige-ink)', fontWeight: 600, fontSize: 14,
          }}>
            {published ? 'Take me off the list' : 'Save as draft'}
          </button>
        </div>

        </div>
      </div>
    </main>
  );
};

export default ReaderProfileEditPage;
